import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  extensionFeedAction,
  extensionFeedQuery,
  setExtensionFeedUseGlobal,
  type ExtensionFeedAction,
  type ExtensionFeedPage,
  type ExtensionFeedReply,
  type ExtensionFeedRow,
  type ExtensionFeedRun,
} from '../../api/settingsSecuritee'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Securitee > Extension Protection, sub lista proprie a firmei: lista
 * GLOBALA OptimumDesk de extensii ransomware (API:
 * `App\Securitee\ExtensionFeed\ExtensionFeed`; jobul: ph4.api
 * `/cron/securitee/extension_feed`, o data pe saptamana).
 *
 *  - comutatorul firmei "use the global list" (implicit pornit) si
 *    excluderile ei: furnizorul de servicii, ca lista proprie;
 *  - "Proposed this week": ce a gasit cautarea AI, fiecare cu adresa sursei;
 *    Approve / Reject numai pentru un administrator de platforma, fiindca lista
 *    e a tuturor firmelor;
 *  - ultima rulare a jobului.
 *
 * TEXTELE sunt propuneri (28 septembrie 2026), nu forma finala.
 */

export const FEED_TITLE = 'Global ransomware extension list'

export const FEED_INTRO =
  'OptimumDesk keeps this list up to date every week from public ransomware lists and from security advisories found by an AI web search. Extensions from public lists are added directly; extensions found by the AI search are proposed first and added only after an OptimumDesk administrator checks the source. Common file types (documents, images, archives, programs) are never added.'

export const FEED_SWITCH = 'Use the global list for this company'

export const FEED_SWITCH_HELP =
  'When on, devices receive the company list above and the global list, without the extensions excluded below. When off, devices receive only the company list.'

export const FEED_LIMIT = (max: number) =>
  `Devices watch at most ${max} extensions: the company list first, then the newest extensions from the global list.`

export const FEED_PROPOSED_TITLE = 'Proposed this week'

export const FEED_PROPOSED_HELP =
  'Found by the AI web search in recent security advisories. Open the source before you approve: an approved extension is sent to the devices of every company that uses the global list.'

export const FEED_PROPOSED_WAITING = 'Waiting for review by an OptimumDesk administrator.'

const RUN_STATUS: Record<string, { label: string; cls: string }> = {
  ok: { label: 'OK', cls: 'label label-success' },
  partial: { label: 'Partial', cls: 'label label-warning' },
  error: { label: 'Failed', cls: 'label label-danger' },
  running: { label: 'Running', cls: 'label label-info' },
}

const AI_STATUS: Record<string, string> = {
  ok: 'AI search done',
  skipped: 'AI search already done this week',
  disabled: 'AI search not configured',
  error: 'AI search failed',
}

function SourceLink({ row }: { row: ExtensionFeedRow }) {
  if (row.sourceUrl === '') return <span>{row.sourceLabel}</span>
  return (
    <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="extfeed-source" title={row.sourceUrl}>
      {row.sourceLabel} <i className="fas fa-external-link-alt" aria-hidden="true"></i>
    </a>
  )
}

export function lastRunText(run: ExtensionFeedRun): string {
  const parts = [`${run.added} added`, `${run.removed} removed`, `${run.proposed} proposed`]
  if (run.held > 0) parts.push(`${run.held} removals held back (a source lost more than 10% of its list)`)
  return parts.join(', ')
}

export function ExtensionFeedPanel() {
  const client = useQueryClient()
  const [search, setSearch] = useState('')
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const page = useQuery(extensionFeedQuery(search))

  if (page.error instanceof ApiError && page.error.status === 403) return null
  if (page.error) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!page.data) return <Spinner />
  const data: ExtensionFeedPage = page.data

  const run = async (call: () => Promise<ExtensionFeedReply>) => {
    if (busy) return
    setBusy(true)
    try {
      const body = await call()
      client.setQueryData(['settings', 'securitee', 'extension_feed', search], body)
      toast(body.message, 'Success', 'success')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }
  const act = (action: ExtensionFeedAction, ext: string) => run(() => extensionFeedAction(action, ext))

  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSearch(typed.trim())
  }

  const shownExcluded = new Set(data.global.map((g) => g.extension))
  const hiddenExcluded = data.excluded.filter((x) => !shownExcluded.has(x.extension))

  return (
    <div className="extfeed-panel" style={{ marginTop: 30 }}>
      <header className="panel-heading">
        <h3>
          <strong>Global</strong> ransomware extensions
        </h3>
        <label className="color">{FEED_TITLE.toLowerCase()}, updated every week</label>
      </header>

      <p className="extfeed-intro">{FEED_INTRO}</p>

      {!data.ready && (
        <div className="alert alert-warning extfeed-not-migrated">
          The global extension list is not available yet: the database migration has not run. Devices receive only the company list.
        </div>
      )}

      {data.ready && (
        <>
          <div className="checkbox extfeed-switch">
            <label>
              <input
                type="checkbox"
                checked={data.useGlobal}
                disabled={!data.canWrite || busy}
                onChange={(e) => void run(() => setExtensionFeedUseGlobal(e.target.checked))}
                aria-label={FEED_SWITCH}
              />{' '}
              <strong>{FEED_SWITCH}</strong>
            </label>
            <span className="help-block">{FEED_SWITCH_HELP}</span>
          </div>
          <p className="text-muted extfeed-limit" style={{ fontSize: 12 }}>
            {FEED_LIMIT(data.agentMax)}
          </p>

          <div className="extfeed-last-run">
            {data.lastRun === null ? (
              <p className="text-muted">The list has not been updated yet.</p>
            ) : (
              <p>
                <strong>Last update:</strong> {data.lastRun.finishedAt || data.lastRun.startedAt}{' '}
                <span className={RUN_STATUS[data.lastRun.status]?.cls ?? 'label label-default'}>{RUN_STATUS[data.lastRun.status]?.label ?? data.lastRun.status}</span>{' '}
                - {lastRunText(data.lastRun)}
                {data.lastRun.aiStatus !== '' && <span className="text-muted"> ({AI_STATUS[data.lastRun.aiStatus] ?? data.lastRun.aiStatus})</span>}
              </p>
            )}
            {data.lastRun !== null && data.lastRun.errors.length > 0 && (
              <ul className="extfeed-errors text-danger" style={{ fontSize: 12 }}>
                {data.lastRun.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            )}
          </div>

          {(data.proposed.length > 0 || data.canDecide) && (
            <>
              <h4 className="bordered" style={{ marginTop: 20 }}>
                {FEED_PROPOSED_TITLE} <small>({data.proposed.length})</small>
              </h4>
              <p className="text-muted" style={{ fontSize: 12 }}>
                {FEED_PROPOSED_HELP}
              </p>
              {data.proposed.length === 0 ? (
                <p className="text-muted extfeed-proposed-empty">Nothing is waiting for review.</p>
              ) : (
                <table className="table table-striped extfeed-proposed">
                  <thead>
                    <tr>
                      <th>Extension</th>
                      <th>Source</th>
                      <th>Details</th>
                      <th>Found</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.proposed.map((row) => (
                      <tr key={row.extension}>
                        <td>
                          <code>{row.pattern}</code>
                        </td>
                        <td style={{ wordBreak: 'break-all' }}>
                          <SourceLink row={row} />
                        </td>
                        <td>{row.note}</td>
                        <td>{row.firstSeen}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {data.canDecide ? (
                            <>
                              <button type="button" className="btn btn-success btn-xs extfeed-approve" disabled={busy} onClick={() => void act('approve', row.extension)}>
                                Approve
                              </button>{' '}
                              <button type="button" className="btn btn-default btn-xs extfeed-reject" disabled={busy} onClick={() => void act('reject', row.extension)}>
                                Reject
                              </button>
                            </>
                          ) : (
                            <span className="text-muted">{FEED_PROPOSED_WAITING}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          <h4 className="bordered" style={{ marginTop: 20 }}>
            Global list <small>({data.total} extensions)</small>
          </h4>
          <form className="form-inline extfeed-search" onSubmit={onSearch} style={{ marginBottom: 10 }}>
            <input
              type="search"
              className="form-control input-sm"
              placeholder="Search an extension"
              aria-label="Search an extension"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />{' '}
            <button type="submit" className="btn btn-default btn-sm">
              Search
            </button>
            {data.matching > data.global.length && (
              <span className="text-muted extfeed-shown" style={{ marginLeft: 10, fontSize: 12 }}>
                Showing the newest {data.global.length} of {data.matching}. Search to find the others.
              </span>
            )}
          </form>
          {data.global.length === 0 ? (
            <p className="text-muted extfeed-global-empty">{data.search !== '' ? 'No extension matches the search.' : 'The global list is empty.'}</p>
          ) : (
            <table className="table table-striped extfeed-global">
              <thead>
                <tr>
                  <th>Extension</th>
                  <th>Source</th>
                  <th>Added</th>
                  <th>Last seen in the source</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.global.map((row) => (
                  <tr key={row.extension} className={row.excluded ? 'text-muted' : undefined}>
                    <td>
                      <code>{row.pattern}</code> {row.excluded && <span className="label label-default extfeed-excluded">Excluded for this company</span>}
                    </td>
                    <td style={{ wordBreak: 'break-all' }}>
                      <SourceLink row={row} />
                    </td>
                    <td>{row.firstSeen}</td>
                    <td>{row.lastSeen}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {data.canWrite &&
                        (row.excluded ? (
                          <button type="button" className="btn btn-default btn-xs extfeed-include" disabled={busy} onClick={() => void act('include', row.extension)}>
                            Include again
                          </button>
                        ) : (
                          <button type="button" className="btn btn-default btn-xs extfeed-exclude" disabled={busy} onClick={() => void act('exclude', row.extension)}>
                            Exclude
                          </button>
                        ))}{' '}
                      {data.canDecide && (
                        <button
                          type="button"
                          className="btn btn-danger btn-xs extfeed-remove"
                          disabled={busy}
                          onClick={() => {
                            if (!confirm(`Remove ${row.pattern} from the global list for every company?`)) return
                            void act('reject', row.extension)
                          }}
                        >
                          Remove for all companies
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {hiddenExcluded.length > 0 && (
            <div className="extfeed-excluded-list">
              <h5>Excluded for this company</h5>
              <ul className="list-group">
                {hiddenExcluded.map((x) => (
                  <li className="list-group-item" key={x.extension}>
                    <code>{x.pattern}</code>
                    {x.status !== 'active' && <span className="text-muted"> (no longer in the global list)</span>}
                    {data.canWrite && (
                      <button type="button" className="pull-right btn btn-default btn-xs extfeed-include" disabled={busy} onClick={() => void act('include', x.extension)}>
                        Include again
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}
